/**
 * The always-on card, from the page (plan.md step 67; the user, 2026-10-15).
 *
 * In the MyRave app, while THIS rave is live and the card is switched on
 * (this phone's setting, `keptLiveCard`), the rave page hands the shell a
 * `LiveCardPlan` built from the STORED bundle — the sets of the next day
 * (shifted by any stage delays, as every screen reads them), the stages with
 * their places from the map's pins (proximity's own `stagePlaces`), the saved
 * sets, the stage this phone was last near — and the shell draws and re-plans
 * the card by itself (`liveCardFor`), offline, with the app closed.
 *
 * Sent again only when the plan CHANGES; `card.end` when the rave stops being
 * live or the card is switched off. Leaving the rave screen does not end it.
 * A browser has no card: nothing is ever posted there.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MAX_CARD_ACTS, MAX_CARD_STAGES, directionOf, stagePlaces, toOffsetISO,
  type LiveCardAct, type LiveCardMode, type LiveCardPlan, type LiveCardWords, type Locale, type MapConfig, type WeatherHour, type WeatherUnits,
} from '@myrave/shared';
import { useEdition } from '@/app/EditionProvider';
import { useLocale } from '@/app/LocaleProvider';
import { intlLocale } from '@/app/rich';
import { translate, type Vars } from '@/i18n';
import { endLiveCard, inNativeApp, keptLiveCard, showLiveCard, type LiveCardSetting } from '@/lib/nativeBridge';
import { useLastStage, useStageAlertSetting } from './proximity';
import { useRaveWeather } from './raveWeather';
import { attendsRave } from '@/offline/OfflineProvider';
import { onAttending } from '@/lib/attendance';

/** How far ahead the plan reaches: the shell re-plans within it, a day of the rave. */
export const CARD_WINDOW_MS = 24 * 60 * 60_000;

interface PlanAct {
  id: string; slug?: string; displayName: string; stageId: string;
  startAtDate: Date; endAtDate: Date; photoUrl?: string;
  artistId?: string; facilitatorId?: string;
}
interface PlanStage { id: string; name: string; color?: string | null; mapPointId?: string | null; bannerUrl?: string | null }

export interface CardPlanInput {
  edition: { id: string; name: string; timezone: string; gatesOpenAt?: string; endsAt?: string; logoUrl?: string; coverUrl?: string; lat?: number | null; lng?: number | null };
  /**
   * The bundle's performers (artists and facilitators): a set with no photo of
   * its own shows its artist's (the user, 2026-10-15: "it does not show the
   * artist image" — 55 of OZORA 2222's sets have only the artist's).
   */
  artists?: readonly { id: string; photoUrl?: string | null }[];
  /** The festival's logo, when the year has none of its own. */
  festivalLogoUrl?: string | null;
  /** The festival's name — "The gates to Imagine" (else the year's name). */
  festivalName?: string | null;
  /** The festival's cover, when the year has none of its own. */
  festivalCoverUrl?: string | null;
  map: Pick<MapConfig, 'points' | 'controlPoints'> | undefined;
  stages: readonly PlanStage[];
  acts: readonly PlanAct[];
  saved: readonly string[];
  stageId: string | null;
  mode: LiveCardMode;
  radiusM: number;
  now: Date;
  /** The rave's own address — where a tap on the card goes (`#live=<slug>`, the user 2026-10-15). */
  url?: string | null;
  /** The rave's forecast (step 65) — the card shows the hour it is in (the user, 2026-10-21). */
  weather?: { units: WeatherUnits; hours: readonly WeatherHour[] } | null;
  /**
   * The reader's language (`useLocale`, a full tag) — the card's words go in
   * it (the user, 2026-10-27: "the languages … live cards, everywhere").
   * Absent: English.
   */
  locale?: Locale;
}

/**
 * The card's words in the reader's language, for the phone to draw as they
 * are (`LIVE_CARD_WORDS`): the phone's native code has no translations, and
 * holds no language list. Whole sentences with their holes — `{clock}`,
 * `{artist}`, `{temp}` are filled on the phone; the gates' name is filled here.
 * The gates' weekday by `Intl`, in the reader's language and the RAVE's day.
 */
export function cardWords(locale: Locale, timezone: string, gates: { at: string; name: string } | null): LiveCardWords {
  const tr = (english: string, vars?: Vars) => translate(locale, english, vars);
  const tag = intlLocale(locale);
  const upper = (text: string) => text.toLocaleUpperCase(tag);
  let gatesDay: string | undefined;
  if (gates) {
    try { gatesDay = new Intl.DateTimeFormat(tag, { weekday: 'long', timeZone: timezone }).format(new Date(gates.at)); } catch { gatesDay = undefined; }
  }
  return {
    // Which way the card runs — Hebrew, Arabic and Persian mirror it (2026-10-27).
    dir: directionOf(locale),
    ...(gates ? {
      gatesTo: tr('The gates to {name}', { name: gates.name }),
      gatesOpenIn: tr('The gates to {name} open in', { name: gates.name }),
    } : {}),
    openIn: tr('open in'),
    openInColon: tr('open in:'),
    endsIn: tr('ends in'),
    startingIn: tr('starting in'),
    switchIn: tr('switch in'),
    starts: tr('starts {clock}'),
    nowPlaying: upper(tr('Now playing')),
    upNextHere: upper(tr('Up next here')),
    yourNext: upper(tr('Your next')),
    nowPlayingArtist: tr('Now playing: {artist}'),
    upNextHereArtist: tr('Up next here: {artist}'),
    yourNextArtist: tr('Your next: {artist}'),
    tapForLatest: tr('Tap for the latest'),
    nothingHere: tr("Nothing on here now · tap for what's on"),
    weatherNow: tr('{temp} now'),
    sunrise: tr('Sunrise {clock}'),
    sunset: tr('Sunset {clock}'),
    ...(gatesDay ? { gatesDay } : {}),
    following: tr('Following you around the rave'),
    followingText: tr("Your card shows the set at the stage you're at."),
    channelName: tr('On now at your stage'),
    channelDescription: tr("The set on now at the stage you're at, and your next one, while a rave is live."),
  };
}

/**
 * The gates as the card's "next" (the user, 2026-10-22, before Imagine: "a
 * live card that the rave is starting today with countdown"): before they
 * open, the plan carries one more set — "Gates open", saved, at the gates — so
 * the card the phones ALREADY run (`liveCardFor`, in the shell) shows YOUR
 * NEXT · Gates open · starts 12:00 and counts down, with no phone build. It
 * starts and ends at the same instant, so it is never "playing": at the gates
 * the card turns over to the rave's own sets by itself.
 */
export const GATES_STAGE_ID = 'gates';
export const gatesActId = (editionId: string) => `gates:${editionId}`;

/** From the rave's local midnight on the gates' day until the gates open — the day the rave starts. */
export function gatesToday(gatesOpenAt: string | null | undefined, timezone: string, now: Date): boolean {
  if (!gatesOpenAt) return false;
  const gates = new Date(gatesOpenAt);
  if (Number.isNaN(gates.getTime()) || now >= gates) return false;
  return toOffsetISO(now, timezone).slice(0, 10) === toOffsetISO(gates, timezone).slice(0, 10);
}

/** An absolute address, so the shell can fetch the photo from anywhere. */
function absolute(url: string | undefined): string | null {
  if (!url) return null;
  try { return new URL(url, typeof location === 'undefined' ? undefined : location.href).href; } catch { return null; }
}

/**
 * The rave's logo as something a widget can draw (the user, 2026-10-15: "no
 * logo … not my rave logo"): a widget cannot render an SVG, and the media
 * server serves an SVG's PNG at `<key>.svg.png` (the emails' rule).
 */
function drawableLogo(url: string | null): string | null {
  if (!url) return null;
  return /\.svg$/i.test(new URL(url).pathname) ? url.replace(/(\.svg)(?=$|[?#])/i, '$1.png') : url;
}

/**
 * The sets ending after `from` and starting before `until`, by start, as the
 * card (and the widgets, step 79) carry them: a set with no photo of its own
 * shows its artist's.
 */
export function cardActs(input: Pick<CardPlanInput, 'acts' | 'artists'>, from: number, until: number, max: number): LiveCardAct[] {
  const performerPhoto = new Map((input.artists ?? []).filter((p) => p.photoUrl).map((p) => [p.id, p.photoUrl!]));
  return input.acts
    .filter((a) => a.endAtDate.getTime() > from && a.startAtDate.getTime() < until)
    .sort((a, b) => a.startAtDate.getTime() - b.startAtDate.getTime() || a.id.localeCompare(b.id))
    .slice(0, max)
    .map((a) => ({
      id: a.id, ...(a.slug ? { slug: a.slug } : {}), name: a.displayName, stageId: a.stageId,
      startAt: a.startAtDate.toISOString(), endAt: a.endAtDate.toISOString(),
      photoUrl: absolute(a.photoUrl
        ?? (a.artistId ? performerPhoto.get(a.artistId) : undefined)
        ?? (a.facilitatorId ? performerPhoto.get(a.facilitatorId) : undefined)),
    }));
}

/** The card's plan from the bundle: pure, so it is tested without a phone. */
export function cardPlanFrom(input: CardPlanInput): LiveCardPlan {
  const locale = input.locale ?? 'en';
  const tr = (english: string, vars?: Vars) => translate(locale, english, vars);
  const t = input.now.getTime();
  const places = new Map((stagePlaces(input.map, input.stages) ?? []).map((p) => [p.stageId, p]));
  const acts = cardActs(input, t, t + CARD_WINDOW_MS, MAX_CARD_ACTS);
  const gatesAt = input.edition.gatesOpenAt ? Date.parse(input.edition.gatesOpenAt) : NaN;
  const beforeGates = Number.isFinite(gatesAt) && t < gatesAt;
  const gatesAct = beforeGates
    ? { id: gatesActId(input.edition.id), name: tr('Gates open'), stageId: GATES_STAGE_ID, startAt: new Date(gatesAt).toISOString(), endAt: new Date(gatesAt).toISOString(), photoUrl: null }
    : null;
  const gates = gatesAct ? { at: gatesAct.startAt, name: input.festivalName || input.edition.name } : null;
  return {
    editionId: input.edition.id,
    rave: input.edition.name,
    timezone: input.edition.timezone,
    endsAt: input.edition.endsAt ?? input.now.toISOString(),
    mode: input.mode,
    stageId: input.stageId,
    radiusM: input.radiusM,
    stages: input.stages.slice(0, MAX_CARD_STAGES).map((s) => {
      const p = places.get(s.id);
      // The stage's banner is its cover — the card's background at that stage (2026-10-16).
      const cover = absolute(s.bannerUrl ?? undefined);
      return { id: s.id, name: s.name, color: s.color ?? null, lat: p?.lat ?? null, lng: p?.lng ?? null, ...(cover ? { coverUrl: cover } : {}) };
    }).slice(0, gatesAct ? MAX_CARD_STAGES - 1 : MAX_CARD_STAGES)
      .concat(gatesAct ? [{ id: GATES_STAGE_ID, name: input.edition.name, color: null, lat: null, lng: null }] : []),
    acts: gatesAct ? [gatesAct, ...acts.slice(0, MAX_CARD_ACTS - 1)] : acts,
    saved: gatesAct ? [gatesAct.id, ...input.saved] : [...input.saved],
    // The gates' own card (the user, 2026-10-22: "the gates to Imagine open in:"), drawn by
    // phones built since; an older build still shows the "Gates open" set above.
    ...(gates ? { gates } : {}),
    // The card's words in the reader's language (2026-10-27) — phones built since draw them.
    words: cardWords(locale, input.edition.timezone, gates),
    ...(input.url ? { url: input.url } : {}),
    ...((logo) => (logo ? { logoUrl: logo } : {}))(drawableLogo(absolute(input.edition.logoUrl ?? input.festivalLogoUrl ?? undefined))),
    ...((cover) => (cover ? { coverUrl: cover } : {}))(absolute(input.edition.coverUrl ?? input.festivalCoverUrl ?? undefined)),
    // The rave's place: the card's next sunrise or sunset (2026-10-15).
    ...(typeof input.edition.lat === 'number' && typeof input.edition.lng === 'number' ? { lat: input.edition.lat, lng: input.edition.lng } : {}),
    // The current weather (the user, 2026-10-21: "like the sunset and sunrise"): the forecast
    // hours from the one it is in, as far as the card plans — the card picks the hour, offline.
    ...(input.weather ? { weather: {
      units: input.weather.units,
      hours: input.weather.hours
        .filter((h) => Date.parse(h.at) + 3_600_000 > t && Date.parse(h.at) < t + CARD_WINDOW_MS)
        .slice(0, 30)
        .map((h) => ({ at: h.at, tempC: h.tempC, condition: h.condition })),
    } } : {}),
  };
}

/**
 * The phone's card setting, read again whenever the page comes back into
 * view — it is chosen on the account page, another host.
 */
function useCardSetting(): LiveCardSetting {
  const [setting, setSetting] = useState(keptLiveCard);
  useEffect(() => {
    const again = () => {
      const next = keptLiveCard();
      setSetting((s) => (s.on === next.on && s.mode === next.mode ? s : next));
    };
    document.addEventListener('visibilitychange', again);
    window.addEventListener('pageshow', again);
    window.addEventListener('focus', again);
    return () => {
      document.removeEventListener('visibilitychange', again);
      window.removeEventListener('pageshow', again);
      window.removeEventListener('focus', again);
    };
  }, []);
  return setting;
}

/**
 * Whether this account ATTENDS the rave — `/me/festivals`, stored-first
 * offline; null until it answers. In the MyRave app only (null outside it).
 */
export function useAttendsRave(editionId: string): boolean | null {
  const native = useMemo(inNativeApp, []);
  const [attends, setAttends] = useState<boolean | null>(null);
  useEffect(() => {
    if (!native) return;
    let on = true;
    void attendsRave(editionId).then((yes) => { if (on) setAttends(yes); });
    // Attended later — by Attend or by saving a set (the user, 2026-10-27): the card starts at once.
    const stop = onAttending((id) => { if (id === editionId && on) setAttends(true); });
    return () => { on = false; stop(); };
  }, [native, editionId]);
  return attends;
}

/**
 * Mounted by `FestivalApp` for the whole rave; does nothing outside the MyRave app.
 * `live` is the rave live OR its gates opening today (`gatesToday`).
 */
export function useLiveCard(live: boolean, saved: readonly string[], now: Date): void {
  const native = useMemo(inNativeApp, []);
  const { bundle, acts, stages } = useEdition();
  const edition = bundle.edition;
  const editionId = edition.id;
  const alerts = useStageAlertSetting();
  const lastStage = useLastStage(editionId);
  const setting = useCardSetting();
  const { locale } = useLocale();
  const savedKey = saved.join(',');
  const minute = Math.floor(now.getTime() / 60_000);
  // The forecast the page already keeps (step 65) — its hours ride on the card's plan.
  const { weather } = useRaveWeather(editionId, bundle);
  // Only a rave this account ATTENDS (the user, 2026-10-15: "while a rave you attend is live") —
  // `/me/festivals`, stored-first offline; until it answers, no card (step 67 walk, 2026-10-21).
  const attends = useAttendsRave(editionId);
  const want = native && live && setting.on && Boolean(edition.endsAt) && attends === true;

  const plan = useMemo(() => (want ? cardPlanFrom({
    edition, artists: bundle.artists, festivalLogoUrl: bundle.festival?.logoUrl ?? null, festivalName: bundle.festival?.name ?? null, festivalCoverUrl: bundle.festival?.coverUrl ?? null, map: bundle.map, stages, acts,
    saved: savedKey ? savedKey.split(',') : [],
    stageId: lastStage, mode: setting.mode, radiusM: alerts.radiusM, now: new Date(minute * 60_000),
    // This page IS the rave's address (its host and year), without the hash.
    url: typeof location === 'undefined' ? null : `${location.origin}${location.pathname.replace(/\/$/, '')}`,
    weather: weather ? { units: weather.units, hours: weather.hours } : null,
    locale,
  }) : null), [want, edition, bundle.artists, bundle.map, stages, acts, savedKey, lastStage, setting.mode, alerts.radiusM, minute, weather, locale]);

  // What the shell was last told: the plan as sent, or 'end'.
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (!native) return;
    // Not known yet whether this account attends: say nothing — a card already on
    // the phone is never ended just because the page is still asking.
    if (attends === null) return;
    if (plan) {
      const json = JSON.stringify(plan);
      if (told.current !== json) { showLiveCard(plan); told.current = json; }
    } else if (told.current !== 'end') {
      endLiveCard(editionId);
      told.current = 'end';
    }
  }, [native, plan, editionId, attends]);
}
